import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../../../lib/queryKeys';
import { newsFeedAdminApi } from '../../newsFeed/api/newsFeedAdmin';
import type { NewsletterSignup } from '../../newsFeed/api/newsFeedAdmin';
import { SettingsSection } from './SettingsSection';

type SortKey = 'email' | 'name' | 'subscribed';

export const NewsletterSubscriptionsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'subscribed',
    dir: 'desc',
  });

  const toggleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const signupsQuery = useQuery({
    queryKey: qk.newsNewsletterSignups(),
    queryFn: newsFeedAdminApi.listNewsletterSignups,
    staleTime: 2 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => newsFeedAdminApi.deleteNewsletterSignup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.newsNewsletterSignups(),
      });
    },
  });

  const signups = signupsQuery.data ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return signups;
    const needle = search.toLowerCase().trim();
    return signups.filter(
      (s) => s.email.toLowerCase().includes(needle) || (s.first_name ?? '').toLowerCase().includes(needle),
    );
  }, [signups, search]);

  const sortedFiltered = useMemo(() => {
    const sortValue = (signup: NewsletterSignup): string | number => {
      switch (sort.key) {
        case 'email':
          return signup.email.toLowerCase();
        case 'name':
          return (signup.first_name ?? '').toLowerCase();
        case 'subscribed':
          return signup.created_at ? new Date(signup.created_at).getTime() : -Infinity;
      }
    };

    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filtered, sort]);

  const handleExportCsv = () => {
    if (!filtered.length) return;
    const header = 'Email,Name,Subscribed At';
    const rows = filtered.map((s) => {
      const name = (s.first_name ?? '').replace(/"/g, '""');
      const date = s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB') : '';
      return `${s.email},"${name}",${date}`;
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <SettingsSection>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems='center'>
          <TextField
            size='small'
            placeholder='Search by email or name...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 360 }}
          />
          <Chip
            label={`${filtered.length} subscriber${filtered.length !== 1 ? 's' : ''}`}
            size='small'
            variant='outlined'
          />
          <Button
            variant='outlined'
            size='small'
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={!filtered.length}
            sx={{ ml: 'auto' }}
          >
            Export CSV
          </Button>
        </Stack>

        {signupsQuery.isLoading ? (
          <Stack direction='row' spacing={1} alignItems='center' sx={{ py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant='body2'>Loading subscriptions...</Typography>
          </Stack>
        ) : signupsQuery.isError ? (
          <Alert severity='error'>Failed to load newsletter subscriptions.</Alert>
        ) : filtered.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {signups.length === 0 ? 'No newsletter subscriptions yet.' : 'No subscriptions match your search.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sort.key === 'email' ? sort.dir : false}>
                    <TableSortLabel
                      active={sort.key === 'email'}
                      direction={sort.key === 'email' ? sort.dir : 'asc'}
                      onClick={() => toggleSort('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sort.key === 'name' ? sort.dir : false}>
                    <TableSortLabel
                      active={sort.key === 'name'}
                      direction={sort.key === 'name' ? sort.dir : 'asc'}
                      onClick={() => toggleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sort.key === 'subscribed' ? sort.dir : false}>
                    <TableSortLabel
                      active={sort.key === 'subscribed'}
                      direction={sort.key === 'subscribed' ? sort.dir : 'asc'}
                      onClick={() => toggleSort('subscribed')}
                    >
                      Subscribed
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='right' />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFiltered.map((signup) => (
                  <SignupRow
                    key={signup.id}
                    signup={signup}
                    onDelete={() => {
                      if (window.confirm(`Remove ${signup.email} from newsletter subscriptions?`)) {
                        deleteMutation.mutate(signup.id);
                      }
                    }}
                    deleting={deleteMutation.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </SettingsSection>
  );
};

const SignupRow: React.FC<{
  signup: NewsletterSignup;
  onDelete: () => void;
  deleting: boolean;
}> = ({ signup, onDelete, deleting }) => {
  const date = signup.created_at
    ? new Date(signup.created_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant='body2'>{signup.email}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' color={signup.first_name ? 'text.primary' : 'text.disabled'}>
          {signup.first_name || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='caption' color='text.secondary'>
          {date}
        </Typography>
      </TableCell>
      <TableCell align='right'>
        <Box
          component='button'
          onClick={onDelete}
          disabled={deleting}
          sx={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'error.main',
            p: 0.5,
            borderRadius: 1,
            display: 'inline-flex',
            '&:hover': { bgcolor: 'error.lighter' },
            '&:disabled': { opacity: 0.4, cursor: 'default' },
          }}
        >
          <DeleteOutlineIcon fontSize='small' />
        </Box>
      </TableCell>
    </TableRow>
  );
};
